/**
 * Vikunja import wizard: connect -> choose -> importing -> done.
 *
 * Openable from the tasks dashboard or preselected from a task list's context
 * menu. The token field is write-only from the app's point of view: it goes to
 * our api for the one read and is never stored.
 */
import { useEffect, useMemo, useState } from "react";
import { create } from "zustand";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowLeft, Check, CircleAlert, Loader2 } from "lucide-react";
import { useAllSpaceItems } from "@/data/store";
import { openItem } from "@/commands/nav";
import { Button } from "@/ui/Button";
import { useDiscord } from "@/discord/useDiscordStore";
import { useVikunjaImport } from "./useVikunjaImport";
import s from "./Vikunja.module.css";

interface DialogState {
  open: boolean;
  preselect: { listId: string; spaceId?: string } | null;
  openImport: (preselect?: { listId: string; spaceId?: string }) => void;
  setOpen: (open: boolean) => void;
}

export const useVikunjaDialog = create<DialogState>((set) => ({
  open: false,
  preselect: null,
  openImport: (preselect) => set({ open: true, preselect: preselect ?? null }),
  setOpen: (open) => {
    if (!open) useVikunjaImport.getState().reset();
    set((st) => ({ open, preselect: open ? st.preselect : null }));
  },
}));

export function VikunjaImportDialog() {
  const { open, preselect, setOpen } = useVikunjaDialog();
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.dialog} aria-describedby={undefined}>
          {open ? <Wizard preListId={preselect?.listId} preSpaceId={preselect?.spaceId} /> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Wizard({ preListId, preSpaceId }: { preListId?: string; preSpaceId?: string }) {
  const { phase, payload, error, progress, summary } = useVikunjaImport();
  const spaces = useAllSpaceItems();
  const discordLinks = useDiscord((st) => st.links);

  const [baseUrl, setBaseUrl] = useState("");
  const [token, setToken] = useState("");
  const [includeDone, setIncludeDone] = useState(true);
  const [footer, setFooter] = useState(true);
  const [listId, setListId] = useState(preListId ?? "");
  const [spaceId, setSpaceId] = useState(preSpaceId ?? "");
  const [newListTitle, setNewListTitle] = useState("Vikunja");
  const [skipped, setSkipped] = useState<Set<number>>(new Set());

  const taskLists = useMemo(
    () =>
      spaces.flatMap((sp) =>
        sp.items
          .filter((i) => i.type === "tasklist" && !i.trashedAt && !i.archivedAt)
          .map((i) => ({
            id: i.id,
            spaceId: sp.kind === "shared" ? sp.spaceId : "",
            title: i.title || "Untitled",
            shared: sp.kind === "shared",
          })),
      ),
    [spaces],
  );

  useEffect(() => {
    void useDiscord.getState().refreshLinks();
  }, []);

  const counts = useMemo(() => {
    if (!payload) return { open: 0, done: 0 };
    const live = payload.tasks.filter((t) => typeof t.project_id !== "number" || !skipped.has(t.project_id));
    return { open: live.filter((t) => t.done !== true).length, done: live.filter((t) => t.done === true).length };
  }, [payload, skipped]);

  const projectIds = useMemo(
    () => (payload ? payload.projects.map((p) => p.id).filter((id) => !skipped.has(id)) : []),
    [payload, skipped],
  );

  const discordLinked = listId ? discordLinks.some((l) => l.listId === listId) : false;

  // ---------- connect ----------
  if (phase === "idle" || phase === "connecting") {
    const busy = phase === "connecting";
    return (
      <>
        <Dialog.Title className={s.title}>Import from Vikunja</Dialog.Title>
        <p className={s.hint}>
          Bring your Vikunja tasks into a Hydra list. Your API token is used for this one read and is never saved.
        </p>
        <label className={s.label} htmlFor="vik-url">
          Vikunja address
        </label>
        <input
          id="vik-url"
          className={s.input}
          placeholder="https://tasks.example.com"
          value={baseUrl}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setBaseUrl(e.target.value)}
        />
        <label className={s.label} htmlFor="vik-token">
          API token
        </label>
        <input
          id="vik-token"
          className={s.input}
          type="password"
          placeholder="tk_..."
          value={token}
          autoComplete="off"
          onChange={(e) => setToken(e.target.value)}
        />
        <p className={s.hint}>
          In Vikunja, open Settings then API Tokens and create one with read access to Tasks and Projects.
        </p>
        <label className={s.check}>
          <input type="checkbox" checked={includeDone} onChange={(e) => setIncludeDone(e.target.checked)} />
          Include completed tasks
        </label>
        {error ? (
          <p className={s.error}>
            <CircleAlert size={14} /> {error}
          </p>
        ) : null}
        <div className={s.actions}>
          <Button
            variant="solid"
            size="sm"
            disabled={busy || !baseUrl.trim() || !token.trim()}
            onClick={() => void useVikunjaImport.getState().connect(baseUrl, token, includeDone)}
          >
            {busy ? "Reading your instance..." : "Connect"}
          </Button>
        </div>
      </>
    );
  }

  // ---------- importing ----------
  if (phase === "importing") {
    const pct = progress.total > 0 ? Math.round((progress.written / progress.total) * 100) : 0;
    return (
      <>
        <Dialog.Title className={s.title}>Importing</Dialog.Title>
        <p className={s.hint}>
          Writing {progress.written} of {progress.total} tasks into {progress.listName}. Keep this tab open until it
          finishes.
        </p>
        <div className={s.progressTrack}>
          <div className={s.progressFill} style={{ width: `${pct}%` }} />
        </div>
      </>
    );
  }

  // ---------- done ----------
  if (phase === "done" && summary) {
    return (
      <>
        <Dialog.Title className={s.title}>
          <Check size={16} /> Import finished
        </Dialog.Title>
        <ul className={s.resultList}>
          <li>
            <strong>{summary.created}</strong> added
          </li>
          <li>
            <strong>{summary.updated}</strong> updated
          </li>
          <li>
            <strong>{summary.skipped}</strong> already up to date
          </li>
        </ul>
        {summary.notes.map((note) => (
          <p key={note} className={s.hint}>
            {note}
          </p>
        ))}
        <p className={s.hint}>
          Run this again any time. Imported tasks remember where they came from, so a second run updates them instead of
          making copies.
        </p>
        <div className={s.actions}>
          <Button
            variant="solid"
            size="sm"
            onClick={() => {
              openItem(summary.listId);
              useVikunjaDialog.getState().setOpen(false);
            }}
          >
            Open the list
          </Button>
        </div>
      </>
    );
  }

  // ---------- choose ----------
  return (
    <>
      <Dialog.Title className={s.title}>Choose what to import</Dialog.Title>
      <p className={s.hint}>
        Connected to {payload?.instance.host} running {payload?.instance.version}. Found {counts.open} open and{" "}
        {counts.done} completed task{counts.done === 1 ? "" : "s"}.
      </p>

      <label className={s.label} htmlFor="vik-list">
        Import into
      </label>
      <select
        id="vik-list"
        className={s.select}
        value={listId}
        onChange={(e) => {
          const next = e.target.value;
          setListId(next);
          setSpaceId(taskLists.find((l) => l.id === next)?.spaceId ?? "");
        }}
      >
        <option value="">Create a new list</option>
        {taskLists.map((l) => (
          <option key={l.id} value={l.id}>
            {l.title}
            {l.shared ? " (shared)" : ""}
          </option>
        ))}
      </select>
      {!listId ? (
        <input
          className={s.input}
          value={newListTitle}
          placeholder="New list name"
          onChange={(e) => setNewListTitle(e.target.value)}
        />
      ) : null}

      {payload && payload.projects.length > 1 ? (
        <>
          <span className={s.label}>Projects</span>
          <div className={s.projectList}>
            {payload.projects.map((p) => (
              <label key={p.id} className={s.check}>
                <input
                  type="checkbox"
                  checked={!skipped.has(p.id)}
                  onChange={(e) => {
                    const next = new Set(skipped);
                    if (e.target.checked) next.delete(p.id);
                    else next.add(p.id);
                    setSkipped(next);
                  }}
                />
                {p.title}
                {p.archived ? <span className={s.badge}>archived</span> : null}
              </label>
            ))}
          </div>
        </>
      ) : null}

      <label className={s.check}>
        <input type="checkbox" checked={footer} onChange={(e) => setFooter(e.target.checked)} />
        Add a details footer for labels, priority and other Vikunja fields
      </label>

      {discordLinked ? (
        <p className={s.warn}>
          <CircleAlert size={14} /> That list is linked to Discord, so every open task imported will post a card there,
          one every couple of seconds.
        </p>
      ) : null}
      {error ? (
        <p className={s.error}>
          <CircleAlert size={14} /> {error}
        </p>
      ) : null}

      <div className={s.actions}>
        <Button variant="ghost" size="sm" onClick={() => useVikunjaImport.getState().reset()}>
          <ArrowLeft size={14} /> Back
        </Button>
        <Button
          variant="solid"
          size="sm"
          disabled={counts.open + counts.done === 0}
          onClick={() =>
            void useVikunjaImport.getState().runImport(
              {
                listId,
                spaceId,
                ...(listId ? { listName: taskLists.find((l) => l.id === listId)?.title } : { newListTitle }),
              },
              { projectIds, footer },
            )
          }
        >
          {phase === "ready" ? <>Import {counts.open + counts.done} tasks</> : <Loader2 size={14} />}
        </Button>
      </div>
    </>
  );
}
